import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Skeleton } from '@mui/material';
import { commentActions } from 'store';
import { MinLoader } from '../loader';

const displayName = (s) => `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown';

// Placeholder rows shown while a fresh search resolves.
const SKELETON_ROWS = Array.from({ length: 5 });

/**
 * Keyboard-navigable dropdown for the @mention suggestion. Rendered by tippy via
 * ReactRenderer (see commentMention.js) — which in TipTap v3 mounts it as a portal
 * inside <EditorContent>, so it lives within the app's Redux Provider.
 *
 * It behaves like SelectAutocompletePagination:
 *   - fetches page 1 on open and on every new `@`-query (debounced, server-side search)
 *   - appends the next page when the list is scrolled near the bottom (infinite scroll)
 *
 * TipTap provides `props.query` (text typed after `@`) and `props.command` (inserts
 * the chosen mention). Item shape passed to command is { id, label }.
 */
export const CommentMentionList = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const query = props.query || '';

    const mentionStaff = useSelector((state) => state.comments.mentionStaff);
    const mentionPage = useSelector((state) => state.comments.mentionPage);
    const mentionHasMore = useSelector((state) => state.comments.mentionHasMore);
    const mentionLoading = useSelector((state) => state.comments.mentionLoading);

    const [selectedIndex, setSelectedIndex] = useState(0);
    // True from the moment the query changes until fresh results land — so the
    // loader is visible during the debounce window too, not just the request.
    const [searching, setSearching] = useState(true);
    const itemRefs = useRef([]);
    const sawLoading = useRef(false);

    const items = useMemo(() => mentionStaff.map((s) => ({ id: s.id, label: displayName(s) })), [mentionStaff]);

    // Debounced page-1 search — reset the list whenever the query changes.
    const search = useMemo(
        () =>
            debounce((value) => {
                dispatch(commentActions.getMentionStaff({ search: value, page: 1 }));
            }, 300),
        [dispatch],
    );

    useEffect(() => {
        setSearching(true);
        search(query);
        return () => search.cancel();
    }, [query, search]);

    // Clear the "searching" flag only after a real request cycle settles
    // (mentionLoading goes false → true → false), so the mount tick doesn't
    // prematurely hide the loader before the debounced fetch fires.
    useEffect(() => {
        if (mentionLoading) sawLoading.current = true;
        else if (sawLoading.current) {
            sawLoading.current = false;
            setSearching(false);
        }
    }, [mentionLoading]);

    const loading = mentionLoading || searching;
    const hasSearch = query.trim().length > 0;
    // A fresh, searched query resolving → replace the list with skeletons.
    // Otherwise (no search value, or paginating) → keep items + small bottom loader.
    const showSkeleton = hasSearch && searching;
    const showBottomLoader = loading && !showSkeleton;

    // Infinite scroll: load the next page when scrolled near the bottom.
    const handleScroll = (event) => {
        const node = event.currentTarget;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 40 && mentionHasMore && !mentionLoading) {
            dispatch(commentActions.getMentionStaff({ search: query, page: mentionPage + 1 }));
        }
    };

    const selectItem = (item) => {
        if (item) props?.command(item);
    };

    // Keep the current selection valid as the list grows/changes.
    useEffect(() => {
        setSelectedIndex((prev) => (prev >= items.length ? 0 : prev));
    }, [items.length]);

    useEffect(() => {
        const el = itemRefs.current[selectedIndex];
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    // Fresh handlers each render so nav sees the latest items/selection.
    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (!items.length) return false;
            if (event.key === 'ArrowUp') {
                setSelectedIndex((selectedIndex + items.length - 1) % items.length);
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex((selectedIndex + 1) % items.length);
                return true;
            }
            if (event.key === 'Enter') {
                selectItem(items[selectedIndex]);
                return true;
            }
            return false;
        },
    }));

    const initialsOf = (label) =>
        (label || '')
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || '?';

    // Keep the popup open in every state (typing, empty search, backspace) — the
    // container always renders while the `@` suggestion is active. An empty search
    // shows an empty-state row instead of collapsing the list.
    return (
        <div className="comment-mention-list" onScroll={handleScroll}>
            {showSkeleton
                ? SKELETON_ROWS.map((_, index) => (
                      <div className="comment-mention-item comment-mention-item-skeleton" key={`skeleton-${index}`}>
                          <Skeleton variant="circular" width={22} height={22} />
                          <Skeleton variant="text" width={120} height={16} />
                      </div>
                  ))
                : items.map((item, index) => (
                      <button
                          ref={(el) => (itemRefs.current[index] = el)}
                          className={`comment-mention-item ${index === selectedIndex ? 'is-selected' : ''}`}
                          key={item.id || index}
                          onClick={() => selectItem(item)}
                          type="button"
                      >
                          <span className="comment-mention-item-avatar">{initialsOf(item.label)}</span>
                          <span className="comment-mention-item-name">{item.label}</span>
                      </button>
                  ))}
            {showBottomLoader && (
                <div className="comment-mention-loading">
                    <MinLoader color="#6558F5" position="relative" margin="0" />
                </div>
            )}
            {!loading && !items.length && <div className="comment-mention-empty">No staff found</div>}
        </div>
    );
});

CommentMentionList.displayName = 'CommentMentionList';
